import { toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import useConversationContainer from '@hooks/useConversationContainer'

import type { CommentModel, ConversationModel, UserModel } from '@models/types'
import { EntityOptions } from '@redux/modules/module'

const defaultState = {
  selectedUsers: [],
}

type SetState = (state: Partial<typeof defaultState>) => void

type BuildActionParams = {
  commentParams: Partial<CommentModel>,
  conversation: Partial<ConversationModel>,
  requestOptions?: EntityOptions,
  selectedUsers: UserModel[],
}

const buildActionParams = (params: BuildActionParams) => {
  const {
    commentParams, conversation, requestOptions, selectedUsers,
  } = params

  const subscribers = selectedUsers.map(selectedUser => ({
    subscribable_id: conversation?.id,
    subscribable_type: 'Conversation',
    user_id: selectedUser.id,
  }))

  const conversationParams = { subscribers }

  return [commentParams, requestOptions, conversationParams]
}

type ToggleUserParams = {
  user: UserModel,
  setState: SetState,
  state: typeof defaultState,
}

const toggleUser = (params: ToggleUserParams) => {
  const { user, setState, state } = params
  const { selectedUsers } = state

  setState({
    // @ts-expect-error TS2345
    selectedUsers: toggleArray(selectedUsers, user, { deepCompare: true }),
  })
}

type CreateSubjectConversationOptions = {
  initConversation: Partial<ConversationModel>,
  defaultMessage?: string,
  requestOptions?: {
    include_comment_user?: true,
    include_conversation_comments?: true,
    include_conversation_subscribers?: true,
    include_image_sizes?: true,
    include_subscriber_user?: true,
    include_user_image?: true,
  },
}

const useCreateSubjectConversation = (params: CreateSubjectConversationOptions) => {
  const { initConversation, defaultMessage, requestOptions } = params
  const { subject_id, subject_type } = initConversation

  const [state, setState] = useSetState(defaultState)
  const { selectedUsers } = state

  const conversationContainerPayload = useConversationContainer({
    comment: {
      body: defaultMessage,
    },
    // @ts-expect-error TS2322
    conversation: initConversation,
    performHttpRequests: !!subject_id && !!subject_type,
    requestOptions,
  })

  const {
    callbacks: { createCommentWithConversation },
    commentForm,
    commentForm: {
      entityState: {
        body,
      },
    },
    conversation,
    creating,
    filteredComments,
    filteredSubscribers,
    filteredSubscribersCount,
    hasFilteredComments,
    hasFilteredSubscribers,
    loading,
  } = conversationContainerPayload

  const lastComment = hasFilteredComments ? filteredComments.slice(-1)[0] : null

  const hasSelectedUsers = !!selectedUsers?.length

  const saveEnabled = !!body && (hasFilteredSubscribers || hasSelectedUsers)

  return {
    callbacks: {
      buildActionParams: (entityParams: Partial<CommentModel>) => buildActionParams({
        conversation, commentParams: entityParams, requestOptions, selectedUsers,
      }),
      createCommentWithConversation,
      toggleUser: (user: UserModel) => (
        toggleUser({ user, setState, state })
      ),
    },
    commentForm,
    conversation,
    creating,
    filteredComments,
    filteredSubscribers,
    filteredSubscribersCount,
    hasFilteredComments,
    hasFilteredSubscribers,
    hasSelectedUsers,
    lastComment,
    loading,
    saveEnabled,
    selectedUsers,
  }
}

export default useCreateSubjectConversation
